import { NgStyle } from '@angular/common';
import { Directive, inject, Input, OnChanges, SimpleChanges } from '@angular/core';
import { mapTokensToVariables, TokenMap, TokenMapper } from '../core/token-mapping';

/**
 * Base Angular directive for applying design system tokens to CSS properties with type safety.
 *
 * This directive serves as the foundation for all design system directives. It accepts a mapping
 * of CSS property names to token values, applies a token mapper function to convert token names
 * to CSS variables, and uses Angular's NgStyle directive to apply the computed styles.
 *
 * This directive is not intended to be used directly. Instead, use the specific token directives
 * like `[pColor]`, `[pSpacing]`, `[pFontSize]`, etc.
 *
 * @template TokenName The type of token values (e.g., ColorName, SpacingName, TypographyVariantName)
 * @selector Not intended for direct use
 * @standalone
 * @internal
 *
 * @example
 * ```typescript
 * // Used internally by other directives
 * @Directive({
 *   selector: '[pColor]',
 *   hostDirectives: [{ directive: DesignSystemStyleDirective, inputs: ['tokenMap:pColor'] }]
 * })
 * export class ColorDirective {
 *   constructor() { setTokenMapper(color); }
 * }
 * ```
 */
@Directive({
    standalone: true,
    hostDirectives: [NgStyle]
})
export class _DesignSystemStyleDirective<TokenName extends string> implements OnChanges {
    /**
     * A mapping of CSS property names to design token values.
     * The token values must be valid for the specific token type T.
     *
     * @example
     * ```html
     * <div [pColor]="{ 'background-color': 'functional-default', 'color': 'base-dark' }">
     * ```
     */
    @Input() public tokenMap: TokenMap<TokenName> = {};

    /**
     * Function that converts token values to CSS variables.
     * Set by the specific directive implementations.
     */
    public mapper!: TokenMapper<TokenName>;

    private readonly ngStyle = inject(NgStyle);

    ngOnChanges(changes: SimpleChanges): void {
        const tokenMap = changes['tokenMap']?.currentValue as TokenMap<TokenName>;
        const mapValue = this.mapper;

        this.ngStyle.ngStyle = tokenMap && mapValue && mapTokensToVariables(tokenMap, mapValue);
        this.ngStyle.ngDoCheck();
    }
}

/**
 * Utility function to set the token mapper for a DesignSystemStyleDirective instance.
 *
 * This function injects the current DesignSystemStyleDirective instance and assigns
 * the provided mapper function to it. Used by specific directive implementations
 * to configure the appropriate token extractor.
 *
 * @template TokenName The type of token values
 * @param tokenMapper Function that converts token values to CSS variables
 *
 * @example
 * ```typescript
 * @Directive({
 *   selector: '[pColor]',
 *   hostDirectives: [{ directive: _DesignSystemStyleDirective, inputs: ['tokenMap:pColor'] }]
 * })
 * export class ColorDirective {
 *   constructor() {
 *     _setTokenMapper(color); // Sets the color extractor as the mapper
 *   }
 * }
 * ```
 */
export function _setTokenMapper<TokenName extends string>(tokenMapper: TokenMapper<TokenName>): void {
    inject(_DesignSystemStyleDirective<TokenName>).mapper = tokenMapper;
}
